import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { usePageLoader } from "@/contexts/page-loader-context";
import { NavigationGuardProvider } from "@/contexts/navigation-guard-context";
import { useAuth } from "@/modules/auth/hooks/use-auth";
import { useTags } from "@/modules/tag/hooks/use-tags";
import { useWidgets } from "@/modules/widget/hooks/use-widgets";
import { WidgetBuilder, type WidgetSaveContext } from "@/modules/widget";
import type { Widget, WidgetUpdateRequest } from "@/modules/widget/types";
import { useBrands, type Brand } from "@/modules/brand";
import { usePlan } from "@/modules/brand";
import { useBrandKits } from "@/modules/brand/hooks/use-brand-kits";
import { WIDGET_TEMPLATE_FALLBACKS } from "@/modules/widget/constants/widget-templates";

export default function WidgetBuilderPage() {
	const { brandUrl, widgetId } = useParams();
	const location = useLocation();
	const justCreated = (location.state as { justCreated?: boolean })?.justCreated === true;
	const { currentWorkspaceId } = useAuth();
	const { showWidget, updateWidget, createWidget, templates, listTemplates, indexPinnedReviewIds, indexCuratedReviewIds, syncPinnedReviews, syncCuratedReviews } = useWidgets();
	const { tags, indexTags } = useTags();
	const { showBrand, selectedBrand } = useBrands();
	const { brandKit, viewBrandKit } = useBrandKits();
	const { planDetails } = usePlan();
	const { showLoader, hideLoader } = usePageLoader();
	const navigate = useNavigate();
	const createAttemptedRef = useRef(false);

	const [widgetData, setWidgetData] = useState<Widget | null>(null);
	const [pinnedReviewIds, setPinnedReviewIds] = useState<string[]>([]);
	const [curatedReviewIds, setCuratedReviewIds] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const [currentBrand, setCurrentBrand] = useState<Brand | null>(null);
	const [isBrandLoading, setIsBrandLoading] = useState(true);
	const [createError, setCreateError] = useState<string | null>(null);

	useEffect(() => {
		const createWidgetIfNeeded = async () => {
			if (widgetId || createAttemptedRef.current) return;
			if (!currentWorkspaceId || !brandUrl) return;

			createAttemptedRef.current = true;
			setIsLoading(true);
			setCreateError(null);

			try {
				// Fetch API templates if not already loaded
				let templateList = templates;
				if (!templateList) {
					const templateResult = await listTemplates();
					if (templateResult.success && templateResult.data) {
						templateList = templateResult.data;
					}
				}

				// Use first API template, fall back to hardcoded fallbacks
				const defaultTemplate = templateList?.[0];
				const fallbackTemplate = WIDGET_TEMPLATE_FALLBACKS[0];
				const response = await createWidget(currentWorkspaceId, {
					title: "Untitled Widget",
					display_type: defaultTemplate?.display_type ?? fallbackTemplate.display_type,
					template: defaultTemplate?.template ?? fallbackTemplate.template,
					template_version: defaultTemplate?.version ?? fallbackTemplate.version,
					content_config: {},
					layout_config: {},
					status: "unpublished",
				});

				if (response.success && response.data) {
					navigate(`/${brandUrl}/widgets/${response.data.id}/edit`, { replace: true });
					return;
				}

				setCreateError("Failed to create widget.");
			} catch (error) {
				setCreateError("Failed to create widget.");
			} finally {
				setIsLoading(false);
			}
		};

		createWidgetIfNeeded();
	}, [brandUrl, widgetId, currentWorkspaceId, createWidget, navigate, templates, listTemplates]);

	const fetchReviewIds = async (workspaceId: string, id: string) => {
		const [pinnedRes, curatedRes] = await Promise.all([indexPinnedReviewIds(workspaceId, id), indexCuratedReviewIds(workspaceId, id)]);
		if (pinnedRes.data) {
			setPinnedReviewIds(pinnedRes.data);
		}
		if (curatedRes.data) {
			setCuratedReviewIds(curatedRes.data);
		}
	};

	useEffect(() => {
		const fetchWidgetData = async () => {
			if (currentWorkspaceId && widgetId) {
				setIsLoading(true);
				try {
					const [widgetRes] = await Promise.all([showWidget(widgetId, currentWorkspaceId), fetchReviewIds(currentWorkspaceId, widgetId)]);
					if (widgetRes.data) {
						setWidgetData(widgetRes.data);
					}
				} catch (error) {
					console.error("Failed to fetch widget data:", error);
				} finally {
					setIsLoading(false);
				}
			}
		};
		fetchWidgetData();
	}, [currentWorkspaceId, widgetId, showWidget]);

	useEffect(() => {
		if (currentWorkspaceId) {
			indexTags(currentWorkspaceId);
		}
	}, [currentWorkspaceId, indexTags]);


	useEffect(() => {
		if (currentWorkspaceId) {
			viewBrandKit(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrandKit]);

	useEffect(() => {
		const fetchBrand = async () => {
			if (brandUrl) {
				setIsBrandLoading(true);
				try {
					const brand = await showBrand(brandUrl);
					if (brand) {
						setCurrentBrand(brand);
					}
				} catch (error) {
					console.error("Failed to fetch brand:", error);
				} finally {
					setIsBrandLoading(false);
				}
			}
		};
		fetchBrand();
	}, [brandUrl, showBrand]);

	useEffect(() => {
		if (isBrandLoading) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [isBrandLoading, showLoader, hideLoader]);

	const handleSave = async (payload: WidgetUpdateRequest, context: WidgetSaveContext): Promise<{ success: boolean; validationErrors?: Record<string, string[]> }> => {
		if (!currentWorkspaceId || !widgetId) return { success: false };

		// Step 1: Update widget settings
		// Remove layout_config if widget_customization is not enabled
		const filteredPayload = planDetails?.plan?.features?.widget_customization === false
			? (({ layout_config, ...rest }) => rest)(payload)
			: payload;
		const response = await updateWidget(widgetId, currentWorkspaceId, filteredPayload);
		if (!response.success && response.validationErrors) {
			return { success: false, validationErrors: response.validationErrors };
		}
		if (!response.success) {
			return { success: false };
		}

		// Step 2: Sync reviews based on type (sequential, after widget update succeeds)
		if (context.widgetType === "single") {
			// For single display type, always sync the curated review
			const singleReviewIds = context.selectedReviewIds.length > 0 ? [context.selectedReviewIds[0]] : [];
			await syncCuratedReviews(currentWorkspaceId, widgetId, singleReviewIds);
		} else if (context.widgetType === "collection") {
			if (context.widgetMode === "dynamic" && context.pinnedReviewsChanged) {
				await syncPinnedReviews(currentWorkspaceId, widgetId, context.pinnedReviewIds);
			}
			if (context.widgetMode === "static" && context.selectedReviewsChanged) {
				await syncCuratedReviews(currentWorkspaceId, widgetId, context.selectedReviewIds);
			}
		}

		// Step 3: Re-fetch canonical widget state and reviews from backend (source of truth)
		const [refreshed] = await Promise.all([showWidget(widgetId, currentWorkspaceId), fetchReviewIds(currentWorkspaceId, widgetId)]);
		if (refreshed.data) {
			setWidgetData(refreshed.data);
		}

		return { success: true };
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">{createError ? createError : "Loading..."}</p>
			</div>
		);
	}

	if (!brandUrl) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">Invalid brand URL</p>
			</div>
		);
	}

	if (!currentWorkspaceId) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">No workspace selected</p>
			</div>
		);
	}

	if (isBrandLoading || !currentBrand) {
		return null;
	}

	if (createError) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">{createError}</p>
			</div>
		);
	}

	if (!widgetData) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">Widget not found</p>
			</div>
		);
	}

	return (
		<NavigationGuardProvider>
			<WidgetBuilder
				brandUrl={brandUrl}
				tags={tags}
				widgetData={widgetData}
				pinnedReviewIds={pinnedReviewIds}
				curatedReviewIds={curatedReviewIds}
				workspaceId={currentWorkspaceId}
				brandKit={brandKit}
				showBranding={currentBrand?.branding !== "disabled"}
				maxReviewsPerWidget={planDetails?.limits?.max_reviews_per_widget}
				allowDynamicWidgets={planDetails?.plan?.features?.dynamic_widgets}
				userRole={selectedBrand?.role}
				onSave={handleSave}
				justCreated={justCreated}
			/>
		</NavigationGuardProvider>
	);
}
